import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { PAGES } from '../../constants/pages';

import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';
import { ActionsAndSave } from 'components/PageHeader';
import { Card, CardContent } from 'components/ui/card';
import { ColumnSelect } from 'components/Select/ColumnSelect';

import { ProductsToRemoveTable } from './components/ProductsToRemoveTable';
import { toast } from 'react-toastify';
import {
    useCreateMultiStockVariationMutation,
    GetTransferableProductsQuery,
    MicrostoreElementState,
    StockVariationType,
    TotadminPermission,
} from 'data/__generated__';

const SELECTABLE_COLUMN_STATES = [MicrostoreElementState.Regular];

export const SiteExternalInventory = () => {
    const [selectedColumnId, setSelectedColumnId] = useState<string | null>(null);
    const [selectedProductsToRemove, setSelectedProductsToRemove] = useState<
        GetTransferableProductsQuery['transferableProducts']
    >([]);

    const [createMultiStockVariation, { loading: loadingCsv }] = useCreateMultiStockVariationMutation();

    useEffect(() => {
        setSelectedProductsToRemove([]);
    }, [selectedColumnId, setSelectedProductsToRemove]);

    const hasNegativeStock = selectedProductsToRemove.some(
        ({ originStockAvailable, quantity }) => originStockAvailable - quantity < 0,
    );

    const handleDeclare = async () => {
        if (!selectedColumnId) {
            return;
        }

        if (hasNegativeStock) {
            toast.error('Impossible de mettre à jour : certains produits ont une nouvelle quantité négative');
            return;
        }

        const { data } = await createMultiStockVariation({
            variables: {
                columnId: selectedColumnId,
                productIdsAndDeltas: selectedProductsToRemove.map(({ productId, quantity }) => ({
                    productId,
                    delta: -quantity,
                })),
                type: StockVariationType.KnownSales,
                explanation: 'Vente connues site - Inventaire externe',
            },
        });
        if (!data) {
            toast.error("Une erreur inconnue est survenue lors de l'enregistrement de l'inventaire");
            return;
        }

        const errors = data.createMultiStockVariation
            .map(({ error }) => error)
            .filter((error): error is string => !!error);

        if (errors.length > 0) {
            toast.error(`Erreurs lors de la mise à jour : ${errors.join(', ')}`);
            return;
        }

        setSelectedColumnId(null);
        toast.success("L'inventaire a bien été enregistré");
    };

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.siteExternalInventory} />
                </HeaderTitle>

                <ActionsAndSave
                    actionGroups={[]}
                    onSave={handleDeclare}
                    saveDisabled={!selectedColumnId || !selectedProductsToRemove.length}
                    saveLoading={loadingCsv}
                    saveTitle="Mettre à jour"
                    saveRequiredPermissions={[TotadminPermission.StockVariationsManage]}
                />
            </Header>
            <ContentContainer>
                <Content>
                    <Card className="bg-white flex-1 min-h-0">
                        <CardContent className="flex flex-1 min-h-0 flex-col gap-4">
                            <ColumnSelect
                                label="Colonne"
                                value={selectedColumnId}
                                onChange={setSelectedColumnId}
                                isClearable
                                dataTest="column-selector"
                                states={SELECTABLE_COLUMN_STATES}
                            />
                            {selectedColumnId ? (
                                <ProductsToRemoveTable
                                    selectedProductsToRemove={selectedProductsToRemove}
                                    setSelectedProductsToRemove={setSelectedProductsToRemove}
                                    columnId={selectedColumnId}
                                    loadingCsv={loadingCsv}
                                />
                            ) : null}
                        </CardContent>
                    </Card>
                </Content>
            </ContentContainer>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ContentContainer = styled.div`
    display: flex;
    flex: 1;
    overflow: hidden;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
    flex: 1;
    overflow: auto;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;
